import { FAST_API_URL } from 'src/config';
import { CreateTeamType, TeamType } from 'src/@types/team';
import { useGet, usePut, useDelete } from './templateHooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAxios } from './useFetch';
import { TeamTypeUpdate } from 'src/@types/team';

export const useTeams = (config = {}) =>
  useGet<TeamType[]>({
    queryKey: ['teams'],
    endpoint: '/v1/teams',
    config: config,
    url: FAST_API_URL,
  });

export const useCreateTeam = () => {
  const queryClient = useQueryClient();
  const createTeam = useAxios<TeamType, CreateTeamType>({
    endpoint: '/v1/teams',
    method: 'post',
    url: FAST_API_URL,
  });

  return useMutation({
    mutationFn: createTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'teammates'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useUpdateTeam = () => {
  const updateTeamMutation = usePut<TeamTypeUpdate, TeamType>(
    ['teams'],
    '/v1/teams',
    undefined,
    undefined,
    FAST_API_URL
  );

  const updateTeam = async (teamID: string, newTeamName: string) => {
    await updateTeamMutation.mutateAsync({
      data: { name: newTeamName },
      resourceId: teamID,
    });
  };

  return { updateTeam, ...updateTeamMutation };
};

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useDelete<{ ids: string[] }, string, TeamType>(
    ['teams'],
    '/v1/teams',
    FAST_API_URL
  );

  const wrappedMutate = (args: { ids: string[] }, options = {}) => {
    mutate(args, {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user'] });
        queryClient.invalidateQueries({ queryKey: ['teammates'] });
      },
    });
  };

  return { mutate: wrappedMutate, ...rest };
};

type UpdateTeamsUsers = {
  user_ids: string[];
};

export function useUpdateTeamsTeammates(team_id: string) {
  const updateTeamsUsersMutation = usePut<UpdateTeamsUsers, undefined>(
    ['user', 'teammates'],
    '/v1/teams/' + team_id + '/users',
    undefined,
    undefined,
    FAST_API_URL
  );
  return updateTeamsUsersMutation;
}
